import { useEffect, useRef, useState } from "react";
import { Grid3x3Icon } from "lucide-react";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/shared/components/ui/popover";
import { cn } from "@/shared/lib/utils";
import type { MatrixColumnDef } from "@/domains/matrix";

// Column keys are already stable slugs, so the mention token is just `@key`.
function columnMentionToken(column: MatrixColumnDef): string {
  return `@${column.key}`;
}

function mentionQueryAtCursor(
  value: string,
  cursor: number,
): { start: number; query: string } | null {
  const before = value.slice(0, cursor);
  const lineStart = before.lastIndexOf("\n") + 1;
  const line = before.slice(lineStart);
  const atInLine = line.lastIndexOf("@");
  if (atInLine === -1) return null;
  const globalAt = lineStart + atInLine;
  const query = line.slice(atInLine + 1);
  if (/\s/.test(query)) return null;
  return { start: globalAt, query };
}

function matchingColumns(
  columns: MatrixColumnDef[],
  query: string,
): MatrixColumnDef[] {
  const normalizedQuery = query.toLowerCase();
  if (!normalizedQuery) return columns;
  return columns.filter((column) => {
    const token = columnMentionToken(column).slice(1).toLowerCase();
    return (
      column.label.toLowerCase().includes(normalizedQuery) ||
      token.includes(normalizedQuery)
    );
  });
}

type PromptTextareaWithColumnMentionsProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  columns: MatrixColumnDef[];
  excludeColumnKey: string | null;
  rows?: number;
  placeholder?: string;
  className?: string;
  // The list renders in a portal, so the editor around it must not treat it as an outside click.
  onMenuOpenChange?: (open: boolean) => void;
};

export function PromptTextareaWithColumnMentions({
  id,
  value,
  onChange,
  columns,
  excludeColumnKey,
  rows = 5,
  placeholder,
  className,
  onMenuOpenChange,
}: PromptTextareaWithColumnMentionsProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState(0);
  const [highlighted, setHighlighted] = useState(0);

  const referenceColumns = columns.filter(
    (column) => column.key !== excludeColumnKey,
  );
  const mention = mentionQueryAtCursor(value, cursor);
  const filtered = mention
    ? matchingColumns(referenceColumns, mention.query)
    : [];
  const menuOpen = Boolean(mention) && referenceColumns.length > 0;

  const mentionResetKey = `${mention?.query ?? ""}|${menuOpen}`;
  const [lastMentionResetKey, setLastMentionResetKey] =
    useState(mentionResetKey);
  if (mentionResetKey !== lastMentionResetKey) {
    setLastMentionResetKey(mentionResetKey);
    setHighlighted(0);
  }

  useEffect(() => {
    if (!menuOpen || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-index="${highlighted}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [highlighted, menuOpen]);

  function insertMention(column: MatrixColumnDef) {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const end = textarea.selectionStart ?? 0;
    const match = mentionQueryAtCursor(textarea.value, end);
    if (!match) return;

    const token = columnMentionToken(column);
    const next = `${textarea.value.slice(0, match.start)}${token} ${textarea.value.slice(end)}`;
    onChange(next);
    // Closed here rather than by the restored caret, which lands a frame later.
    const pos = match.start + token.length + 1;
    setCursor(pos);
    onMenuOpenChange?.(false);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    });
  }

  // Single path for the caret and the report upwards, so the editor stays in step.
  function syncCursor(textarea: HTMLTextAreaElement) {
    const next = textarea.selectionStart ?? 0;
    setCursor(next);
    onMenuOpenChange?.(
      Boolean(mentionQueryAtCursor(textarea.value, next)) &&
        referenceColumns.length > 0,
    );
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const textarea = event.currentTarget;
    const cur = textarea.selectionStart ?? 0;
    const match = mentionQueryAtCursor(textarea.value, cur);
    const list = match ? matchingColumns(referenceColumns, match.query) : [];
    const canMention = Boolean(match) && list.length > 0;

    if (!canMention) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((i) => (i + 1) % list.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((i) => (i - 1 + list.length) % list.length);
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      const column = list[highlighted];
      if (column) insertMention(column);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (!match) return;
      const next =
        textarea.value.slice(0, match.start) + textarea.value.slice(cur);
      onChange(next);
      setCursor(match.start);
      onMenuOpenChange?.(false);
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(match.start, match.start);
      });
    }
  }

  return (
    <div className="space-y-1">
      {/* A popover rather than an absolutely placed box, which the scrolling
          editor around it clipped after the first entry. */}
      <Popover open={menuOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Textarea
              id={id}
              ref={textareaRef}
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
                syncCursor(e.target);
              }}
              onClick={(e) => syncCursor(e.currentTarget)}
              onKeyUp={(e) => syncCursor(e.currentTarget)}
              onSelect={(e) => syncCursor(e.currentTarget)}
              onKeyDown={handleKeyDown}
              rows={rows}
              placeholder={placeholder}
              className={cn(
                "text-xs focus-visible:border-primary focus-visible:ring-primary/20",
                className,
              )}
              aria-autocomplete={menuOpen ? "list" : undefined}
              aria-controls={
                menuOpen ? "matrix-column-mention-list" : undefined
              }
              aria-expanded={menuOpen}
            />
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="start"
          sideOffset={4}
          // The caret stays in the prompt, which handles every key the list answers to.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          className="max-h-44 w-[var(--radix-popover-trigger-width)] overflow-auto p-0"
        >
          {filtered.length > 0 ? (
            <div ref={listRef} id="matrix-column-mention-list" role="listbox">
              <p className="text-muted-foreground px-2 py-1 text-[10px] font-medium tracking-wide uppercase">
                Insert column reference
              </p>
              {filtered.map((column, index) => (
                <button
                  key={column.key}
                  type="button"
                  role="option"
                  data-index={index}
                  aria-selected={index === highlighted}
                  className={cn(
                    "flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs",
                    index === highlighted
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted/80",
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMention(column);
                  }}
                  onMouseEnter={() => setHighlighted(index)}
                >
                  <Grid3x3Icon className="size-3.5 shrink-0 opacity-70" />
                  <span className="min-w-0 flex-1 truncate">
                    {column.label}
                  </span>
                  <span className="text-muted-foreground shrink-0 font-mono text-[10px]">
                    {columnMentionToken(column)}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground px-2 py-2 text-xs">
              No matching columns
            </p>
          )}
        </PopoverContent>
      </Popover>

      <p className="text-muted-foreground text-[10px]">
        {referenceColumns.length === 0 ? (
          "Add another column to reference it with @."
        ) : (
          <>
            Type{" "}
            <kbd className="bg-muted rounded border px-1 font-mono text-[10px]">
              @
            </kbd>{" "}
            to insert a column reference
          </>
        )}
      </p>
    </div>
  );
}
